import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import { useRouteMatch } from "react-router-dom";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import {
  Radio,
  Select
} from "src/library/components/Forms";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import ScheduleWarningModal from "src/core/schedules/components/ScheduleWarningModal";
import ScheduleDayPicker from "src/core/schedules/components/ScheduleDayPicker";
import {
  UpdateScheduleParams,
  ISchedule,
  SchedulePreRequestUpdate,
  ScheduleMonthlyFrequency,
  ScheduleType,
  useScheduleApi
} from "src/core/schedules/api/schedules.api";
import useList from "src/core/lists/hooks/useList";
import useCanSubmitStandingOrder from "src/core/lists/hooks/useCanSubmitStandingOrder";
import { IList } from "src/core/lists/api/lists.api";
import {
  IDeliveryDays,
  WeekDay
} from "src/core/drops/api/drops.api";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { media } from "src/library/utils/styled-media-queries/index";

import { scheduleSchema } from "./ScheduleSchema";

interface IScheduleFormProps {
  list: IList | null;
  schedule: ISchedule | null;
  availableDays?: IDeliveryDays;
  maxStandingOrderDate: Date;
  scheduleTerminology: string;
  handleClose: () => void;
  handleSetSchedule: (form: IScheduleFormData) => void;
  isScheduleLoading: boolean;
  isLoading: boolean;
}

export interface IScheduleFormData {
  startDate?: Date;
  endDate?: Date;
  monthlyFrequency?: ScheduleMonthlyFrequency;
  scheduleDays?: WeekDay[];
  schduleType?: ScheduleType;
}

const ScheduleEditForm: React.ReactFCWithChildren<IScheduleFormProps> = props => {
  const { list, schedule } = props;
  const initialStart = schedule?.startDate ? moment.utc(schedule.startDate).toDate() : moment.utc().toDate();
  const initialEnd = (schedule && schedule.endDate && moment.utc(schedule.endDate).toDate()) || undefined;
  const [ preUpdateScheduleResponse, preUpdateSchedule ] = useScheduleApi("PRE_UPDATE_SCHEDULE");
  const [ scheduleUpdateWarningModal, setScheduleUpdateWarningModal ] = React.useState<SchedulePreRequestUpdate | null>(null);
  const match = useRouteMatch<{ listId: string }>();
  const listId = match ? parseInt(match.params.listId, 0) : 0;
  const [ { currentList }, { saveList } ] = useList(listId);
  const canSubmitStandingOrder = useCanSubmitStandingOrder();

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const canSave = canSubmitStandingOrder(currentList.data);

  const initialValues: IScheduleFormData = (schedule && {
    scheduleDays: schedule.scheduleDays,
    endDate: initialEnd,
    startDate: initialStart,
    monthlyFrequency: schedule.monthlyFrequency,
    schduleType: schedule.type
  }) || {
    endDate: initialEnd,
    startDate: initialStart,
    schduleType: ScheduleType.Manual
  };

  const [ form, setForm ] = React.useState<IScheduleFormData>(initialValues);

  const handleFormChange = React.useCallback(
    (name: keyof IScheduleFormData, value: unknown) => {
      setValidation({
        error: false,
        message: ""
      });

      setForm({
        ...form,
        [ name ]: value
      });
    },
    [ form ]
  );

  // On changing schedule type, amend form endDate respectively
  const handleRadioChange = React.useCallback((value: ScheduleType) => {
    const formUpdate = { ...form };

    // set end date to undefined if ongoing is selected
    if (value === ScheduleType.Ongoing) {
      formUpdate.endDate = undefined;
    } else {
      // reset end date to schedule end date if exists
      formUpdate.endDate = schedule && schedule.endDate ? moment.utc(schedule.endDate).toDate() : undefined;
    }

    formUpdate.schduleType = value;
    setForm(formUpdate);
  }, [ form, schedule ]);

  const handleSubmit = React.useCallback(() => {
    if (form.schduleType === ScheduleType.Manual && !form.endDate) {
      return toast.error("An end date is required for fixed date standing orders");
    }

    return scheduleSchema
      .validate(form)
      .then(() => {
        if (list) {
          const scheduleParams: UpdateScheduleParams = {
            ...schedule,
            scheduleDays: form.scheduleDays,
            listId: list.id,
            endDate: form.endDate,
            startDate: form.startDate,
            bankHolidaysDelivery: 1,
            monthlyFrequency: form.monthlyFrequency,
            validating: false,
            type: form.schduleType
          };

          if (currentList.mode === "edit") {
            saveList();
          }

          preUpdateSchedule({ schedule: scheduleParams });
        }
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [
    form,
    list,
    schedule,
    currentList.mode,
    preUpdateSchedule,
    saveList
  ]);

  React.useEffect(() => {
    if (preUpdateScheduleResponse.data) {
      setScheduleUpdateWarningModal(preUpdateScheduleResponse.data);
    }

    if (preUpdateScheduleResponse.error) {
      toast.error(preUpdateScheduleResponse.error.message);
    }
  }, [ preUpdateScheduleResponse ]);

  const loading = preUpdateScheduleResponse.pending || props.isScheduleLoading;

  return (
    <FormContainer className={schedule && schedule.status === "suspended" ? "suspended" : "enabled"}>
      <ScheduleWarningModal
        data={scheduleUpdateWarningModal}
        handleDismiss={() => setScheduleUpdateWarningModal(null)}
        onConfirm={() => props.handleSetSchedule(form)}
      />

      <div className="row">
        <FormLine className="schedule-frequency left-side">
          <LineHeader>
            Select frequency
          </LineHeader>

          <LineInput className="schedule-frequency-select">
            <Select
              as="select"
              label=""
              name="monthlyFrequency"
              value={form.monthlyFrequency}
              handleChange={e => handleFormChange("monthlyFrequency", e.target.value)}
              unselectedOption={{
                text: "Please Select",
                value: ""
              }}
              options={[
                {
                  text: "Every week",
                  value: "1"
                },
                {
                  text: "Every 2 weeks",
                  value: "2"
                },
                {
                  text: "Every 3 weeks",
                  value: "3"
                },
                {
                  text: "Every 4 weeks",
                  value: "4"
                }
              ]}
            />
          </LineInput>
        </FormLine>

        <FormLine
          style={{
            flexDirection: "column",
            alignItems: "normal"
          }}
          className="right-side"
        >
          <LineHeader>
            Select which days
          </LineHeader>

          <LineInput>
            <ScheduleDayPicker
              availableDays={props.availableDays}
              value={form.scheduleDays ? form.scheduleDays : []}
              handleChange={days => handleFormChange("scheduleDays", days)}
            />
          </LineInput>
        </FormLine>

      </div>

      <div className="row">
        <FormLine
          style={{ position: "relative" }}
          className="left-side"
        >
          <LineHeader>
            Select type of standing order
          </LineHeader>

          <LineInput>
            <Radio
              options={[
                {
                  text: "Ongoing",
                  value: ScheduleType.Ongoing,
                  checked: form.schduleType === ScheduleType.Ongoing
                },
                {
                  text: "Fixed Dates",
                  value: ScheduleType.Manual,
                  checked: form.schduleType === ScheduleType.Manual
                }
              ]}
              handleChange={e => handleRadioChange(e.target.value)}
              name="scheuleType"
              label=""
              value={form.schduleType}
            />

          </LineInput>
        </FormLine>

        <FormLine
          style={{ position: "relative" }}
          className="right-side"
        >
          <LineHeader>
            Set start and end date
          </LineHeader>

          <div className="selected-dates">
            <div className="form-date-input">
              <Text>
                Start Date
              </Text>

              <DateInput
                label="Start Date"
                date={moment.utc(form.startDate).toDate()}
                setDate={date => handleFormChange("startDate", date)}
                minimumDate={new Date()}
              />
            </div>

            <div className="form-date-input">
              <Text>
                End Date
              </Text>

              <DateInput
                label="End Date"
                date={form.endDate ? moment.utc(form.endDate).toDate() : undefined}
                setDate={date => handleFormChange("endDate", date)}
                minimumDate={form.startDate ? moment.utc(form.startDate).toDate() : new Date()}
                maximumDate={moment.utc().add(2, "years").toDate()}
                disabled={form.schduleType === ScheduleType.Ongoing}
              />
            </div>
          </div>
        </FormLine>
      </div>

      {validation.error && (
        <ValidationContainer>
          <Icon
            $fillColor="error"
            icon={Icons.ERRORCIRCLE}
          />

          {validation.message}
        </ValidationContainer>
      )}

      <SubmitContainer>
        <Button
          disabled={props.isLoading || canSave !== null}
          loading={loading}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          <>
            {schedule ? `Update ${props.scheduleTerminology}` : `Create ${props.scheduleTerminology}`}
          </>
        </Button>

        {schedule && (
          <Button
            type="reset"
            onClick={() => props.handleClose()}
          >
            <>
              Discard changes
            </>
          </Button>
        )}
      </SubmitContainer>
    </FormContainer>
  );
};

const ValidationContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    color: ${theme.colors.error};
    display: flex;
    align-items: center;

    svg {
      margin-right: ${theme.spacing.padding.sm};
    }
  `}
`;

const LineHeader = styled(Text)`
  display: flex;
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing.padding.sm};
  `}
`;

const SubmitContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.lg};
    > * {
      margin: 0 ${theme.spacing.padding.xs};
    }

    > button:first-child {
      margin: 0 -${theme.spacing.padding.xs};
    }
  `}
`;

const FormLine = styled.div`
  display: flex;
  align-items: left;
  flex-direction: column;

  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
  `}
`;

const LineInput = styled.div`
  position: relative;
`;

const FormContainer = styled.div`
  ${({ theme }) => css`
    .row {
      display: flex;
      flex-direction: column;
    }

    .left-side {
      flex: 1;
    }

    .right-side {
      flex: 2;
    }

    ${media({ gt: theme.breakpoints.xl })} {
      .row {
        flex-direction: row;
      }

      .right-side {
        margin-left: ${theme.spacing.margins.xl};
      }
    }

    .schedule-frequency {
      height: 131px;  

      .schedule-frequency-select {
        margin-top: ${theme.spacing.padding.sm};
      }
    }

    .selected-dates {
      display: flex;
      margin-top: ${theme.spacing.margins.sm};

      .form-date-input {
        margin-right: ${theme.spacing.margins.xl};

        div:first-of-type {
          margin-top: ${theme.spacing.padding.xs};
        }
      }
    }
    
    &.suspended ${FormLine} {
      opacity: 0.5;
      pointer-events: none;
    }
  `}
`;

export default ScheduleEditForm;
